import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { useScope } from "@state/scopeContext.lib";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import { EditableCellContent } from "@design-system/ViewTable/EditableCellContent";
import { CaptureClicks } from "@design-system/CaptureClicks/CaptureClicks";
import { useUpdateClientEntityMutation } from "@api/organizationClient.api";
import { fullDateFullYearFormat } from "@common/helpers/date";
import { format, parseISO } from "date-fns";
import { ClientDetailsRow } from "../clientViewConfig";
import styles from "./ClientViewCell.module.scss";
import { EMPTY_CELL_VALUE } from "./lib";

export const StartDateCell = ({
    row,
    getValue,
}: CellContext<ViewTableFeatures, ClientDetailsRow, string | null>) => {
    const { organizationId } = useScope();
    const updateEntity = useUpdateClientEntityMutation();

    if (row.original.type === "workspace") {
        return EMPTY_CELL_VALUE;
    }

    const workspace = row.getParentRow()?.original;
    const value = getValue();
    const entityId = row.original.id;

    if (!entityId || !organizationId || !workspace) {
        return null;
    }

    const dateOnlyValue = value ?? undefined;
    const formattedValue = dateOnlyValue
        ? format(parseISO(dateOnlyValue), fullDateFullYearFormat)
        : undefined;

    return (
        <ViewTableCell>
            <div className={styles.startDateCell}>
                <CaptureClicks>
                    <DatePicker
                        mode="day"
                        variant="panel-input"
                        clearable={false}
                        showTriggerIcon={false}
                        displayFormat={fullDateFullYearFormat}
                        placeholder="Add start date"
                        value={dateOnlyValue}
                        onValueChange={(newDate) => {
                            if (!newDate) {
                                return;
                            }

                            updateEntity.mutate({
                                organizationId,
                                clientId: workspace.id,
                                entityId: Number(entityId),
                                payload: {
                                    bookkeepingStartDate: newDate,
                                },
                            });
                        }}
                        trigger={
                            <EditableCellContent
                                isEmpty={!dateOnlyValue}
                                addText="Add start date"
                                hideIcon
                            >
                                {formattedValue}
                            </EditableCellContent>
                        }
                    />
                </CaptureClicks>
            </div>
        </ViewTableCell>
    );
};
